import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { NdaAgreement } from "@/components/NdaAgreement";
import { TermsConsent } from "@/components/TermsConsent";
import orbitIcon from "@/assets/orbit-icon.png";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { onAuth } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ndaAccepted) {
      setError("Please read and agree to the Beta Participant NDA to continue.");
      return;
    }
    if (!termsAccepted) {
      setError("Please agree to the Terms and Conditions and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, nda_accepted: true, nda_signature: name, terms_accepted: true });
      onAuth(data);
      nav("/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col items-center justify-center text-center gap-6 px-10 py-8 text-white overflow-hidden" style={{ background: "radial-gradient(120% 90% at 30% 20%, #0d2350 0%, #071232 45%, #050b20 100%)" }}>
        <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.30), transparent 70%)" }} />
        <span className="relative z-10 font-display text-xl font-extrabold tracking-tight">Project Management <span className="text-[#7ea6ff]">Orchestrator</span></span>
        <img src={orbitIcon} alt="Project Management Orchestrator" className="relative z-10 w-full max-w-[190px] object-contain drop-shadow-2xl" data-testid="register-earth" />
        <div className="relative z-10 max-w-md">
          <p className="overline text-white/70 mb-2">For project managers</p>
          <h1 className="font-display text-3xl xl:text-4xl font-extrabold tracking-tight leading-[1.1]">
            Describe the goal. Get a best-practice project plan in seconds.
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <div className="flex items-center gap-2.5 mb-8">
            <img src={orbitIcon} alt="Orbit" className="h-11 w-auto" />
            <span className="font-display text-2xl font-extrabold tracking-tight">Project Management Orchestrator</span>
          </div>
          <p className="overline text-muted-foreground mb-2">Get started</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">Create your account</h2>

          {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="register-error">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password-input" className="mt-1.5" />
            </div>
            <NdaAgreement accepted={ndaAccepted} onAcceptedChange={setNdaAccepted} />
            <TermsConsent accepted={termsAccepted} onAcceptedChange={setTermsAccepted} />
          </div>

          <Button type="submit" disabled={loading || !ndaAccepted || !termsAccepted} className="w-full mt-6 h-11" data-testid="register-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
          </Button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="go-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
